/**
 * PricingSection Component
 * Full pricing section with Stripe checkout integration
 * Shows Individual and Business plans with license selector
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  Slider,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import StarIcon from '@mui/icons-material/Star';
import { 
  subscriptionService, 
  BUSINESS_TIERS, 
  calculateBusinessPrice 
} from '../services/subscriptionService';

interface PricingSectionProps {
  onCheckoutStarted?: () => void;
}

export function PricingSection({ onCheckoutStarted }: PricingSectionProps) {
  const [planType, setPlanType] = useState<'individual' | 'business'>('individual');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [licenseCount, setLicenseCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const individualPlans = subscriptionService.getPricingPlans();
  const businessInfo = subscriptionService.getBusinessPricingInfo();
  
  const businessPricing = calculateBusinessPrice(licenseCount);

  const handleIndividualCheckout = async () => {
    setLoading(true);
    setError(null);
    onCheckoutStarted?.();
    
    const planId = billingCycle === 'monthly' ? 'individual_monthly' : 'individual_yearly';
    const result = await subscriptionService.initiateStripeCheckout(planId);
    
    if (result.error) {
      setError(result.error);
      setLoading(false);
    }
    // If successful, user is redirected to Stripe
  };

  const handleBusinessCheckout = async () => {
    if (licenseCount < 5) {
      setError('Minimum 5 licenses required for business plans');
      return;
    }
    
    setLoading(true);
    setError(null);
    onCheckoutStarted?.();
    
    const result = await subscriptionService.initiateBusinessCheckout(licenseCount);
    
    if (result.error) {
      setError(result.error);
      setLoading(false);
    }
    // If successful, user is redirected to Stripe
  };

  return (
    <Card
      sx={{
        backgroundColor: 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(20px)',
        borderRadius: 4,
        border: '2px solid rgba(6, 182, 212, 0.3)',
        boxShadow: '0 0 40px rgba(6, 182, 212, 0.15)',
        overflow: 'hidden',
        maxWidth: 700,
        width: '100%'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%)',
        borderBottom: '1px solid rgba(6, 182, 212, 0.2)'
      }}>
        <Typography variant="h5" sx={{ color: '#06b6d4', fontWeight: 700, textAlign: 'center', mb: 1 }}>
          Choose Your Plan
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
          7-day free trial • Cancel anytime • Instant access
        </Typography>
      </Box>

      {/* Plan Type Tabs */}
      <Box sx={{ px: 3, pt: 3 }}>
        <Tabs
          value={planType}
          onChange={(_, v) => setPlanType(v)}
          variant="fullWidth"
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600,
              '&.Mui-selected': {
                color: '#06b6d4'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#06b6d4'
            }
          }}
        >
          <Tab 
            icon={<PersonIcon />} 
            iconPosition="start" 
            label="Individual" 
            value="individual" 
          />
          <Tab 
            icon={<GroupsIcon />} 
            iconPosition="start" 
            label="Business" 
            value="business"
          />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Individual Plan Section */}
        {planType === 'individual' && (
          <Box>
            {/* Billing Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
              <Chip
                label="Monthly"
                onClick={() => setBillingCycle('monthly')}
                variant={billingCycle === 'monthly' ? 'filled' : 'outlined'}
                sx={{
                  px: 2,
                  backgroundColor: billingCycle === 'monthly' ? 'rgba(96, 165, 250, 0.3)' : 'transparent',
                  borderColor: 'rgba(96, 165, 250, 0.5)',
                  color: billingCycle === 'monthly' ? '#60a5fa' : 'rgba(255, 255, 255, 0.6)',
                  fontWeight: 600
                }}
              />
              <Chip
                label="Annual (2 months FREE)"
                onClick={() => setBillingCycle('yearly')}
                variant={billingCycle === 'yearly' ? 'filled' : 'outlined'}
                icon={billingCycle === 'yearly' ? <StarIcon sx={{ color: '#fbbf24 !important' }} /> : undefined}
                sx={{
                  px: 2,
                  backgroundColor: billingCycle === 'yearly' ? 'rgba(251, 191, 36, 0.2)' : 'transparent',
                  borderColor: billingCycle === 'yearly' ? 'rgba(251, 191, 36, 0.5)' : 'rgba(96, 165, 250, 0.5)',
                  color: billingCycle === 'yearly' ? '#fbbf24' : 'rgba(255, 255, 255, 0.6)',
                  fontWeight: 600
                }}
              />
            </Box>

            {/* Price Display */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h2" sx={{ color: '#60a5fa', fontWeight: 800 }}>
                ${billingCycle === 'monthly' ? '10' : '100'}
                <Typography component="span" variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 1 }}>
                  /{billingCycle === 'monthly' ? 'month' : 'year'}
                </Typography>
              </Typography>
              {billingCycle === 'yearly' && (
                <Typography variant="body2" sx={{ color: '#22c55e', mt: 1 }}>
                  Save $20/year (2 months free!)
                </Typography>
              )}
            </Box>

            {/* Features */}
            <Box sx={{ mb: 4 }}>
              {individualPlans[0]?.features.map((feature, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* CTA Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              onClick={handleIndividualCheckout}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CreditCardIcon />}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)',
                }
              }}
            >
              {loading ? 'Starting Checkout...' : 'Start 7-Day Free Trial'}
            </Button>
          </Box>
        )}

        {/* Business Plan Section */}
        {planType === 'business' && (
          <Box>
            {/* License Slider */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                How many licenses do you need?
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Slider
                  value={licenseCount}
                  onChange={(_, v) => setLicenseCount(v as number)}
                  min={5}
                  max={300}
                  step={1}
                  marks={[
                    { value: 5, label: '5' },
                    { value: 25, label: '25' },
                    { value: 100, label: '100' },
                    { value: 250, label: '250' },
                    { value: 300, label: '300+' }
                  ]}
                  sx={{
                    flex: 1,
                    color: '#a78bfa',
                    '& .MuiSlider-markLabel': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.7rem'
                    }
                  }}
                />
                <TextField
                  type="number"
                  value={licenseCount}
                  onChange={(e) => setLicenseCount(Math.max(5, parseInt(e.target.value) || 5))}
                  inputProps={{ min: 5, max: 1000 }}
                  sx={{
                    width: 90,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(167, 139, 250, 0.5)' }
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Tier Indicator */}
            {businessPricing && (
              <Box sx={{ 
                p: 2, 
                mb: 3, 
                borderRadius: 2, 
                backgroundColor: 'rgba(167, 139, 250, 0.1)',
                border: '1px solid rgba(167, 139, 250, 0.3)'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label={`${businessPricing.tier.name} Tier`}
                    sx={{ 
                      backgroundColor: 'rgba(167, 139, 250, 0.3)',
                      color: '#a78bfa',
                      fontWeight: 700
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    {businessPricing.tier.minUsers}-{businessPricing.tier.maxUsers || '∞'} users
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#a78bfa', fontWeight: 800, textAlign: 'center' }}>
                  ${businessPricing.totalMonthly}
                  <Typography component="span" variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 1 }}>
                    /month
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', mt: 1 }}>
                  ${businessPricing.perUser}/user × {licenseCount} users
                </Typography>
              </Box>
            )}

            {/* Tier Breakdown */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
              {BUSINESS_TIERS.map((tier) => (
                <Box 
                  key={tier.id}
                  sx={{ 
                    px: 2, 
                    py: 1, 
                    borderRadius: 2,
                    backgroundColor: businessPricing?.tier.id === tier.id 
                      ? 'rgba(167, 139, 250, 0.2)' 
                      : 'rgba(255, 255, 255, 0.05)',
                    border: businessPricing?.tier.id === tier.id 
                      ? '1px solid rgba(167, 139, 250, 0.5)' 
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center',
                    minWidth: 80
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                    {tier.name}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: businessPricing?.tier.id === tier.id ? '#a78bfa' : 'rgba(255, 255, 255, 0.8)', 
                    fontWeight: 700 
                  }}>
                    ${tier.pricePerUser}/user
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem' }}>
                    {tier.minUsers}-{tier.maxUsers || '∞'}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Charity Banner */}
            <Box sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(251, 146, 60, 0.1) 100%)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <VolunteerActivismIcon sx={{ color: '#22c55e', fontSize: 32 }} />
              <Box>
                <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 700 }}>
                  20% goes to Packs for Hunger 🎒
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Feeding children in Lea County, NM
                </Typography>
              </Box>
            </Box>

            {/* Features */}
            <Box sx={{ mb: 4 }}>
              {businessInfo.features.map((feature, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <CheckCircleIcon sx={{ color: '#a78bfa', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* CTA Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || licenseCount < 5}
              onClick={handleBusinessCheckout}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CreditCardIcon />}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
                }
              }}
            >
              {loading ? 'Starting Checkout...' : `Start Trial for ${licenseCount} Users`}
            </Button>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 3, pb: 3, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Payment secured by Stripe 🔒 • Auto-charges after 7-day trial if not canceled
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

export default PricingSection;
